import { useState, useEffect } from "react";
import { Form, message } from "antd";
import dayjs from "dayjs";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../variables/api/axiosInstance";
import { LOGGED_USER } from "../../../variables/constants";
import { isSameYear } from "../../../variables/functions";
import { useResume } from "../../../components/services/ResumeProvider";
import {
    buildEnginPayload, computeImmatriculation, getEmptyConducteurForm,
    getEmptyEnginForm, mapApiPersonToSouscripteurForm, mapEnginToUpdateForm
} from "../../../pages/production/contrats/AddContrat/data/donnees";

export const isTpvCategory = (cat, usage, categories = []) => {
    const code = (typeof cat === "string" ? cat : cat?.codeCategorie) || "";
    const resolvedName =
        (typeof cat === "string"
            ? (categories.find((c) => c.codeCategorie === cat || c.nom === cat)?.nom || "")
            : cat?.nom) || "";
    const upperName = resolvedName.toUpperCase();
    const upperCode = code.toUpperCase();
    const upperUsage = (usage || "").toUpperCase();
    return (
        upperCode.includes("TPV") ||
        upperName.includes("TPV") ||
        upperName.includes("TRANSPORT") ||
        upperUsage === "TPV" ||
        upperUsage.includes("TRANSPORT")
    );
};

const useContratForm = () => {
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState(1);
    const [loading, setLoading] = useState(false);
    const [oldInsured, setOldInsured] = useState(false);
    const [showConducteurModal, setShowConducteurModal] = useState(false);
    const [currentVehicleIndex, setCurrentVehicleIndex] = useState(0);
    const [exonereDeTaxe, setExonereDeTaxe] = useState(false);
    const [categories, setCategories] = useState([]);
    const [marques, setMarques] = useState([]);
    const [professions, setProfessions] = useState([]);
    const [nonCompulsoryGuaranties, setNonCompulsoryGuaranties] = useState([]);
    const [selectedCategorie, setSelectedCategorie] = useState("");
    const [categorie, setCategorie] = useState("");
    const [carosseries, setCarosseries] = useState([]);
    const [fractionnements, setFractionnements] = useState([]);
    const [preview, setPreview] = useState("");
    const [dateEffet, setDateEffet] = useState(dayjs());
    const [fractionnementSelect, setFractionnementSelect] = useState("");
    const [selectedFractionnement, setSelectedFractionnement] = useState("");
    const [loadingEdition, setLoadingEdition] = useState(false);
    const [sameYear, setSameYear] = useState(true);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [oldClient, setOldClient] = useState("");
    const [cSelected, setCSelected] = useState(false);
    const [clientSearch, setClientSearch] = useState("");
    const [clients, setClients] = useState([]);
    const [editMode, setEditMode] = useState(false);
    const [tauxCommission, setTauxCommission] = useState(0);
    const [showExcelImport, setShowExcelImport] = useState(false);
    const [showFlotteImport, setShowFlotteImport] = useState(false);
    const [agencyProduction, setAgencyProduction] = useState("");
    const [formData, setFormData] = useState([getEmptyEnginForm()]);
    const [personneData, setPersonneData] = useState(getEmptyConducteurForm());

    const navigate = useNavigate();
    const location = useLocation();
    const param = useParams();
    const { pushState, consumeForPage } = useResume();
    const restoredResume = consumeForPage("new-add-contrat");

    // Chargement initial des référentiels
    useEffect(() => {
        axiosInstance.get(`/api/categorie`).then((res) => {
            if (!param?.contratId) {
                setSelectedCategorie(res.data[0]?.codeCategorie);
                setCategorie(res.data[0]);
            }
            setCategories(res.data);
        }).catch(console.error);

        axiosInstance.get(`/api/parametre/type/PROFESSION`).then((res) => setProfessions(res.data)).catch(console.error);
        axiosInstance.get(`/api/parametre/type/CAROSSERIE`).then((res) => setCarosseries(res.data)).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Restauration du contexte de navigation (useResume)
    useEffect(() => {
        if (!restoredResume?.data) return;
        const data = restoredResume.data;
        setActiveTab(data.activeTab ?? 1);
        const restoredEngins = (data.formData || [getEmptyEnginForm()]).map((fd) => ({
            immatriculationFacultative: false, ...fd,
        }));
        setFormData(restoredEngins);
        setPersonneData(data.personneData || getEmptyConducteurForm());
        setSelectedCategorie(data.selectedCategorie || "");
        setCategorie(data.categorie || "");
        setFractionnementSelect(data.fractionnementSelect || "");
        setSelectedFractionnement(data.selectedFractionnement || "");
        setExonereDeTaxe(!!data.exonereDeTaxe);
        setDateEffet(data.dateEffet ? dayjs(data.dateEffet) : dayjs());
        setOldInsured(!!data.oldInsured);
        setTauxCommission(data.tauxCommission || 0);
        form.setFieldsValue({
            engins: restoredEngins,
            souscripteur: data.personneData || getEmptyConducteurForm(),
            dateEffet: data.dateEffet ? dayjs(data.dateEffet) : dayjs(),
        });
    }, [restoredResume, form]);

    // Synchronisation Form ↔ state local
    useEffect(() => {
        form.setFieldsValue({ engins: formData, souscripteur: personneData, dateEffet });
    }, [formData, personneData, dateEffet, form]);

    // Recherche clients (debounced via state)
    useEffect(() => {
        axiosInstance.get(`/api/personne/clients`, { params: { page: 0, size: 10, search: clientSearch } })
            .then((res) => {
                setClients(res.data?.content?.map((clt) => ({
                    label: clt.prenom ? `${clt.nom} ${clt.prenom}` : `${clt.nom}`,
                    value: JSON.stringify(clt), key: clt.codeId,
                })) || []);
            }).catch(console.error);
    }, [clientSearch]);

    // Rechargement marques + fractionnements selon catégorie sélectionnée
    useEffect(() => {
        axiosInstance.get(`/api/parametre/type/MARQUE`).then((res) => {
            const mqs = res.data?.filter(m => m.libelleParametre);
            setMarques(selectedCategorie ? mqs?.filter(m => m.categorie?.codeCategorie === selectedCategorie) : mqs);
        }).catch(console.error);

        if (selectedCategorie) {
            axiosInstance.get(`/api/fractionnement/categorie/code/${selectedCategorie}`)
                .then((res) => setFractionnements(res.data)).catch(console.error);
            someEffects(selectedCategorie);
            handleValeurVenale(selectedCategorie, categories);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCategorie]);

    // Persistance de l'état du formulaire via useResume
    useEffect(() => {
        const effetIso = dateEffet && typeof dateEffet?.toDate === "function"
            ? dateEffet.toDate().toISOString() : null;

        const slimFormData = (formData || []).map((fd) => ({
            categorie: fd.categorie, marque: fd.marque, numeroSerie: fd.numeroSerie,
            matricule: fd.matricule, typeSerie: fd.typeSerie, immatriculation: fd.immatriculation,
            immatriculationFacultative: fd.immatriculationFacultative, puissance: fd.puissance,
            nombrePlace: fd.nombrePlace, energie: fd.energie, numeroChassis: fd.numeroChassis,
            valeurDeclaree: fd.valeurDeclaree, valeurVenale: fd.valeurVenale, valeurANeuf: fd.valeurANeuf,
            dateMiseEnCirculation: fd.dateMiseEnCirculation, usage: fd.usage, carosserie: fd.carosserie,
            genre: fd.genre, rc: fd.rc, useSouscripteur: fd.useSouscripteur,
            personne: { nom: fd.personne?.nom, prenom: fd.personne?.prenom, telephone: fd.personne?.telephone, genre: fd.personne?.genre },
        }));
        const slimPersonne = {
            nom: personneData?.nom, prenom: personneData?.prenom, genre: personneData?.genre,
            telephone: personneData?.telephone, adresse: personneData?.adresse, email: personneData?.email,
        };
        pushState({
            path: `${location.pathname}${location.search}`,
            module: "CONTRATS",
            summary: "Contrat (nouvelle saisie) en cours",
            payload: {
                page: "new-add-contrat",
                data: {
                    activeTab, formData: slimFormData, personneData: slimPersonne,
                    selectedCategorie, categorie, fractionnementSelect, selectedFractionnement,
                    dateEffet: effetIso, exonereDeTaxe, oldInsured, tauxCommission,
                },
            },
        });
    }, [activeTab, formData, personneData, selectedCategorie, categorie, fractionnementSelect,
        selectedFractionnement, dateEffet, exonereDeTaxe, oldInsured, tauxCommission, location, pushState]);

    // Chargement du contrat en mode édition
    useEffect(() => {
        if (!param?.contratId) return;
        setEditMode(true);
        setLoadingEdition(true);
        axiosInstance.get(`/api/contrat/codeId/${param?.contratId}`).then((res) => {
            const contratFromApi = res.data;
            setAgencyProduction(contratFromApi.courtier ? contratFromApi?.courtier : contratFromApi?.producteur?.agence);
            someEffects(contratFromApi?.engins[0]?.categorie?.codeCategorie);
            const mappedFormData = contratFromApi.engins.map((engin, index) => {
                const assured = engin.assure || contratFromApi.assure;
                if (!isSameYear(engin.dateMiseEnCirculation)) setSameYear(false);
                return mapEnginToUpdateForm(engin, assured, index);
            });
            setLoadingEdition(false);

            axiosInstance.get(`/api/garantie/categorie/non-compulsory/code/${contratFromApi?.engins[0]?.categorie?.codeCategorie}/${contratFromApi?.organisme?.code}`)
                .then((res) => setNonCompulsoryGuaranties(res.data)).catch(console.error);

            axiosInstance.get(`/api/parametre/type/MARQUE`).then((res) => {
                const mqs = (res.data || [])
                    .filter(el => el?.categorie?.nom?.trim().startsWith(contratFromApi?.engins[0]?.categorie?.nom.trim().slice(0, 4)))
                    .sort((a, b) => (a.libelleParametre || "").localeCompare(b.libelleParametre || "", "fr", { sensitivity: "base" }));
                setMarques(mqs);
            }).catch(console.error);

            const persData = mapApiPersonToSouscripteurForm(contratFromApi.souscripteur);
            setExonereDeTaxe(contratFromApi.souscripteur?.exonereDeTaxe || false);
            setPersonneData(persData);

            const engin = contratFromApi?.engins[0];
            if (engin.categorie) {
                setSelectedCategorie(engin.categorie?.codeCategorie);
                form.setFieldValue(['engins', 0, 'categorie'], engin?.categorie?.codeCategorie);
                setCategorie(engin.categorie);
            }

            const fs = fractionnements?.find(f => f.fractionnementId === parseInt(contratFromApi?.fractionnement?.fractionnementId));
            setFractionnementSelect(fs ? fs?.fractionnementId : "");
            form.setFieldValue("engins", mappedFormData);
            setFormData(mappedFormData);
            setSelectedFractionnement(contratFromApi?.fractionnement?.nombre || null);
        }).catch(console.error);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [param?.contratId, categories, fractionnements]);

    const someEffects = (value) => {
        axiosInstance.get(`/api/garantie/categorie/non-compulsory/code/${value}/${LOGGED_USER?.profil?.organisme?.code}`)
            .then((res) => setNonCompulsoryGuaranties(res.data)).catch(console.error);
    };

    const handleValeurVenale = (categorieCode, cats) => {
        const cat = cats.find(c => c.codeCategorie === categorieCode);
        if (cat) {
            setFormData(prev => {
                const next = prev.map(e => ({
                    ...e,
                    valeurVenale: cat.valeurVenaleMinimale || 0,
                    valeurDeclaree: cat.valeurVenaleMinimale || 0,
                    valeurANeuf: cat.valeurVenaleMinimale || 0,
                }));
                form.setFieldsValue({ engins: next });
                return next;
            });
        }
    };

    const onFormValuesChange = (_, allValues) => {
        if (allValues.engins) setFormData(allValues.engins);
    };

    const handleVehicleChangeManual = (index, field, value) => {
        let newFormData = [...formData];
        newFormData[index][field] = value;

        if (field === 'categorie') {
            if (value !== "M") {
                newFormData[index].usage = "TAXI";
                newFormData[index].genre = null;
                if (newFormData.length > 1) newFormData = [newFormData[0]];
            }
            setCategorie(categories.find((c) => c.codeCategorie === value));
            setSelectedCategorie(value);
            someEffects(value);
            handleValeurVenale(value, categories);
        }

        if (['numeroSerie', 'matricule', 'typeSerie', 'categorie'].includes(field)) {
            const catValue = newFormData[index]?.categorie || selectedCategorie;
            newFormData[index].immatriculation = computeImmatriculation(
                catValue, newFormData[index].typeSerie,
                newFormData[index].numeroSerie, newFormData[index].matricule
            );
        }

        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
    };

    const toggleImmatriculationFacultative = (index, checked) => {
        setFormData(prev => {
            const next = [...prev];
            if (!next[index]) return prev;
            next[index] = { ...next[index], immatriculationFacultative: checked };
            return next;
        });
        const engins = form.getFieldValue('engins') || [];
        if (engins[index]) {
            engins[index] = { ...engins[index], immatriculationFacultative: checked };
            form.setFieldsValue({ engins });
        }
    };

    const addVehicle = () => {
        if (!categorie?.codeCategorie?.startsWith("M")) {
            message.warning('Vous ne pouvez ajouter plusieurs véhicules que pour la catégorie MOTO');
            return;
        }
        const newVehicle = {
            ...formData[0], numeroSerie: "", matricule: "", immatriculation: "",
            numeroChassis: "", personne: getEmptyConducteurForm(), useSouscripteur: true,
        };
        const newFormData = [...formData, newVehicle];
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Nouveau véhicule ajouté');
    };

    const copyFirstVehicleData = () => {
        if (formData.length < 2) return;
        const newFormData = [...formData];
        const lastIndex = formData.length - 1;
        const firstVehicle = formData[0];
        newFormData[lastIndex] = {
            ...firstVehicle, numeroSerie: "", matricule: "", immatriculation: "",
            numeroChassis: "",
            personne: firstVehicle.useSouscripteur
                ? getEmptyConducteurForm()
                : { ...firstVehicle.personne, codeId: "", contact: [] },
            useSouscripteur: firstVehicle.useSouscripteur,
        };
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Données copiées avec succès');
    };

    const removeVehicle = (index) => {
        if (formData.length === 1) {
            message.warning('Vous devez avoir au moins un véhicule');
            return;
        }
        const newFormData = formData.filter((_, i) => i !== index);
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success('Véhicule supprimé');
    };

    const openConducteurModal = (index) => {
        setCurrentVehicleIndex(index);
        setShowConducteurModal(true);
    };

    const handleConducteurSave = (conducteur) => {
        const newFormData = [...formData];
        newFormData[currentVehicleIndex].personne = {
            ...conducteur,
            contact: conducteur.telephone ? [conducteur.telephone] : (conducteur.contact || []),
        };
        const hasConducteurInfo = conducteur.nom || conducteur.prenom || conducteur.adresse || conducteur.telephone;
        newFormData[currentVehicleIndex].useSouscripteur = !hasConducteurInfo;
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        setShowConducteurModal(false);
        message.success('Conducteur enregistré');
    };

    const validateVehicleForm = async (forceImmatriculation = false) => {
        const engs = form.getFieldValue('engins') || formData;
        const names = [];

        engs.forEach((e, i) => {
            names.push(['engins', i, 'categorie']);
            const validateImmat = forceImmatriculation || !isTpvCategory(e?.categorie || selectedCategorie, e?.usage) || !e?.immatriculationFacultative;
            if (validateImmat) {
                names.push(['engins', i, 'typeSerie'], ['engins', i, 'matricule']);
                if ((e && e.typeSerie) === 'TG') names.push(['engins', i, 'numeroSerie']);
            }
            names.push(
                ['engins', i, 'marque'], ['engins', i, 'energie'], ['engins', i, 'usage'],
                ['engins', i, 'volume'], ['engins', i, 'nombrePlace'], ['engins', i, 'genre'],
                ['engins', i, 'moteur'], ['engins', i, 'type'], ['engins', i, 'numeroChassis'],
                ['engins', i, 'valeurVenale'], ['engins', i, 'valeurDeclaree'],
                ['engins', i, 'valeurANeuf'], ['engins', i, 'dateMiseEnCirculation']
            );
            const cat = e?.categorie ?? (engs[0]?.categorie);
            if (cat?.codeCategorie?.startsWith("M")) names.push(['engins', i, 'carosserie']);

            if (e?.dateMiseEnCirculation) {
                if (!isSameYear(e.dateMiseEnCirculation?.toISOString())) setSameYear(false);
            }
        });

        try {
            await form.validateFields(names);
            if (forceImmatriculation) {
                const missingImmat = engs.some(e =>
                    !e?.typeSerie || !e?.matricule || ((e?.typeSerie === 'TG') && !e?.numeroSerie)
                );
                if (missingImmat) {
                    message.error("Veuillez renseigner l'immatriculation pour valider le contrat.");
                    return false;
                }
            }
            return true;
        } catch (err) {
            const firstError = err?.errorFields?.[0];
            if (firstError?.name) {
                try { form.scrollToField(firstError.name); } catch (e) { /* ignore */ }
            }
            return false;
        }
    };

    const validateSouscripteur = async (sous) => {
        try {
            const phonePattern = /^(22|90|91|92|93|96|98|99|70|71|79|97)\d{6}$/;
            if (!sous?.contact || !sous.contact[0] || !phonePattern.test(sous.contact[0])) {
                message.error('Numéro de téléphone du souscripteur invalide');
                return false;
            } else if (!sous?.nom && sous.typePersonne === 'MORALE') {
                message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
                return false;
            } else if ((!sous?.nom || !sous?.prenom) && sous.typePersonne === 'PHYSIQUE') {
                message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
                return false;
            } else if (!sous?.adresse) {
                message.error('Adresse du souscripteur manquante');
                return false;
            } else if (!sous?.genre) {
                message.error('Genre du souscripteur manquant');
                return false;
            }
            return true;
        } catch {
            message.error('Veuillez remplir tous les champs obligatoires du souscripteur');
            return false;
        }
    };

    const showPreview = async () => {
        try {
            setLoadingPreview(true);
            const fract = fractionnementSelect
                ? fractionnements.find(e => e.fractionnementId === parseInt(fractionnementSelect))
                : null;
            const engins = formData.map(fd => buildEnginPayload(fd, selectedCategorie, categories));
            const contratData = { engins, dateEffet: dateEffet?.toISOString() || dayjs().toISOString(), fractionnement: fract };
            const response = await axiosInstance.post(`/api/contrat/new-preview`, contratData);
            setPreview(response.data);
            setLoadingPreview(false);
        } catch (err) {
            console.error(err);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { if (activeTab === 2) showPreview(); }, [fractionnementSelect, dateEffet]);

    const handleSubmit = async () => {
        const vehOk = await validateVehicleForm();
        if (!vehOk) return;
        if (!oldInsured) {
            const sousOk = await validateSouscripteur(personneData);
            if (!sousOk) return;
        }

        setLoading(true);
        const engins = (form.getFieldValue('engins') || formData).map(e => ({
            ...e,
            dateMiseEnCirculation: e?.dateMiseEnCirculation ? e.dateMiseEnCirculation.toISOString() : null,
        }));
        const fract = fractionnementSelect
            ? fractionnements.find(e => e.fractionnementId === parseInt(fractionnementSelect))
            : null;
        const payload = {
            engins: engins.map(fd => buildEnginPayload(fd, selectedCategorie, categories)),
            souscripteur: personneData,
            dateEffet: dateEffet?.toISOString() || dayjs().toISOString(),
            fractionnement: fract,
            tauxCommission,
            organisme: JSON.parse(sessionStorage.getItem('currentCompagnie')) || LOGGED_USER?.profil?.organisme,
            courtier: agencyProduction || null,
        };

        let url = `/api/contrat/new`;
        if (oldInsured) {
            payload.oldSouscripteur = oldClient;
            url = `/api/contrat/new/old-client`;
        }

        const onSuccess = (res) => {
            setLoading(false);
            navigate(`/details-contrat/${res.data.codeId}`, { replace: true });
            message.success(param?.contratId ? 'Contrat mis à jour avec succès!' : 'Contrat créé avec succès!');
        };
        const onError = (err) => {
            console.error(err);
            message.error('Une erreur est survenue');
            setLoading(false);
        };

        if (param?.contratId) {
            payload.codeId = param.contratId;
            axiosInstance.put(url, payload).then(onSuccess).catch(onError);
        } else {
            axiosInstance.post(url, payload).then(onSuccess).catch(onError);
        }
    };

    const handleExcelImportSuccess = (importedData) => {
        if (importedData.length === 0) return;
        const firstContract = importedData[0];
        setPersonneData(firstContract.souscripteur);
        setExonereDeTaxe(firstContract.souscripteur.exonereDeTaxe);
        const enginData = [{ ...getEmptyEnginForm(), ...firstContract.engin }];
        setFormData(enginData);
        form.setFieldsValue({ engins: enginData, souscripteur: firstContract.souscripteur, dateEffet: firstContract.dateEffet });
        setDateEffet(firstContract.dateEffet);
        setTauxCommission(firstContract.tauxCommission);
        const cat = categories.find(c => c.codeCategorie === firstContract.engin.categorie);
        if (cat) { setSelectedCategorie(cat.codeCategorie); setCategorie(cat); }
        message.success('Données importées avec succès!');
    };

    const handleFlotteImportSuccess = (importedEngins) => {
        if (importedEngins.length === 0) return;
        const newFormData = importedEngins.map(engin => ({
            ...engin, categorie: formData[0]?.categorie || selectedCategorie,
        }));
        setFormData(newFormData);
        form.setFieldsValue({ engins: newFormData });
        message.success(`${importedEngins.length} engin(s) importé(s) avec succès dans la flotte!`);
        const allSameYear = importedEngins.every(e =>
            !e.dateMiseEnCirculation || isSameYear(e.dateMiseEnCirculation.toISOString())
        );
        setSameYear(allSameYear);
    };

    return {
        form, activeTab, setActiveTab, loading, oldInsured, setOldInsured,
        showConducteurModal, setShowConducteurModal, currentVehicleIndex,
        exonereDeTaxe, setExonereDeTaxe, categories, marques, professions,
        nonCompulsoryGuaranties, selectedCategorie, categorie,
        carosseries, fractionnements, preview, dateEffet, setDateEffet,
        fractionnementSelect, setFractionnementSelect, selectedFractionnement,
        loadingEdition, sameYear, loadingPreview, oldClient, setOldClient,
        cSelected, setCSelected, clientSearch, setClientSearch, clients,
        editMode, tauxCommission, setTauxCommission,
        showExcelImport, setShowExcelImport, showFlotteImport, setShowFlotteImport,
        agencyProduction, setAgencyProduction, formData, setFormData,
        personneData, setPersonneData, param,
        onFormValuesChange, handleVehicleChangeManual, toggleImmatriculationFacultative,
        addVehicle, copyFirstVehicleData, removeVehicle,
        openConducteurModal, handleConducteurSave, validateVehicleForm,
        handleSubmit, showPreview, handleExcelImportSuccess, handleFlotteImportSuccess,
    };
};

export default useContratForm;
